import { Check, ChevronDown, Menu, Plus, X } from 'lucide-react';
import React from 'react';
import type { useWorkspace } from '../../contexts/WorkspaceContext';
import { initialsFrom } from '../../utils/formatters';
import { isWorkspaceScopedPage } from '../../config/pages';

type WorkspaceContextShape = ReturnType<typeof useWorkspace>;

type TopbarProps = Pick<
  WorkspaceContextShape,
  | 'workspaceLoading'
  | 'activeOrgId'
  | 'activeProjectId'
  | 'activeOrganization'
  | 'activeProject'
  | 'activePage'
  | 'workspaceMenuOpen'
  | 'userMenuOpen'
  | 'setAppMenuOpen'
  | 'setWorkspaceMenuOpen'
  | 'setUserMenuOpen'
  | 'setOrgMenuOpen'
  | 'setEnvMenuOpen'
  | 'selectOrganization'
  | 'selectApp'
  | 'selectNav'
> & {
  organizations: WorkspaceContextShape['workspace']['organizations'];
  apps: WorkspaceContextShape['workspace']['apps'];
  userDisplayName?: string;
  userEmail?: string;
  onOpenOrganizationDialog: () => void;
  onOpenAccountSecurity: () => void;
  onSignOut: () => void;
  mobileNavOpen: boolean;
  onToggleMobileNav: () => void;
};

const Topbar = React.memo(function Topbar({
  onOpenOrganizationDialog,
  onOpenAccountSecurity,
  onSignOut,
  mobileNavOpen,
  onToggleMobileNav,
  organizations,
  apps,
  userDisplayName,
  userEmail,
  workspaceLoading,
  activeOrgId,
  activeProjectId,
  activeOrganization,
  activeProject,
  activePage,
  workspaceMenuOpen,
  userMenuOpen,
  setAppMenuOpen,
  setWorkspaceMenuOpen,
  setUserMenuOpen,
  setOrgMenuOpen,
  setEnvMenuOpen,
  selectOrganization,
  selectApp,
  selectNav
}: TopbarProps) {
  const profileUser = userDisplayName
    ? {
        name: userDisplayName,
        email: userEmail || '',
        avatar: initialsFrom(userDisplayName)
      }
    : {
        name: 'Unknown user',
        email: '',
        avatar: '?'
      };
  const workspaceScoped = isWorkspaceScopedPage(activePage);

  function closeOtherMenus() {
    setAppMenuOpen(false);
    setOrgMenuOpen(false);
    setUserMenuOpen(false);
    setEnvMenuOpen(false);
  }

  return (
    <header className="topbar">
      <div className="topbarLeft">
        <button
          type="button"
          className="mobileNavToggle"
          onClick={onToggleMobileNav}
          aria-controls="primary-navigation"
          aria-expanded={mobileNavOpen}
          aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
        >
          {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        <button
          type="button"
          className="scopeSwitcherButton"
          onClick={() => {
            setWorkspaceMenuOpen((open) => !open);
            closeOtherMenus();
          }}
          aria-expanded={workspaceMenuOpen}
          aria-haspopup="dialog"
          aria-label="Change workspace or project"
        >
          <span className="scopeSwitcherMark">
            {activeOrganization?.mark || '?'}
          </span>
          <span className="scopeSwitcherCopy">
            <strong className="scopeSwitcherValue">
              <span>
                <small>Workspace</small>
                {activeOrganization?.name || 'Choose workspace'}
              </span>
              <span className="scopeSwitcherArrow" aria-hidden="true">
                ›
              </span>
              <span>
                <small>{workspaceScoped ? 'Viewing' : 'Project'}</small>
                {workspaceScoped
                  ? 'All projects'
                  : activeProject?.name || 'Choose project'}
              </span>
            </strong>
          </span>
          <ChevronDown
            size={14}
            className={`topbarOrgCaret${workspaceMenuOpen ? ' open' : ''}`}
          />
        </button>

        {workspaceMenuOpen ? (
          <div
            className="scopeSwitcherPanel"
            role="dialog"
            aria-label="Choose workspace or project"
          >
            <div className="scopeSwitcherHeader">
              <strong>Choose where you&apos;re working</strong>
              <span>
                Workspace pages include every project. Project pages show one
                project.
              </span>
            </div>
            <div className="scopeSwitcherColumns">
              <section className="scopeSwitcherColumn" aria-label="Workspaces">
                <div className="scopeSwitcherColumnLabel">Workspace</div>
                <div className="scopeSwitcherList">
                  {organizations.map((organization) => {
                    const active = organization.id === activeOrgId;
                    return (
                      <button
                        key={organization.id}
                        type="button"
                        className={`scopeSwitcherItem${active ? ' active' : ''}`}
                        onClick={() => {
                          if (!active) selectOrganization(organization.id);
                        }}
                      >
                        <span className="scopeSwitcherItemMark">
                          {organization.mark}
                        </span>
                        <span className="scopeSwitcherItemCopy">
                          <strong>{organization.name}</strong>
                          <small>{organization.subtitle}</small>
                        </span>
                        {active ? <Check size={14} /> : null}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="scopeSwitcherUtility"
                  onClick={() => {
                    onOpenOrganizationDialog();
                    setWorkspaceMenuOpen(false);
                  }}
                >
                  <Plus size={13} />
                  New workspace
                </button>
              </section>

              <section className="scopeSwitcherColumn" aria-label="Projects">
                <div className="scopeSwitcherColumnLabel">Project</div>
                {workspaceLoading ? (
                  <div className="scopeSwitcherLoading">Loading projects…</div>
                ) : apps.length > 0 ? (
                  <div className="scopeSwitcherList">
                    {apps.map((app) => {
                      const active = app.id === activeProjectId;
                      return (
                        <button
                          key={app.id}
                          type="button"
                          className={`scopeSwitcherItem${active ? ' active' : ''}`}
                          onClick={() => {
                            selectApp(app.id);
                            setWorkspaceMenuOpen(false);
                          }}
                        >
                          <span className="scopeSwitcherItemMark">
                            {app.mark}
                          </span>
                          <span className="scopeSwitcherItemCopy">
                            <strong>{app.name}</strong>
                            <small>{app.platform}</small>
                          </span>
                          {active ? <Check size={14} /> : null}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="scopeSwitcherLoading">
                    No projects in this workspace.
                  </div>
                )}
                <button
                  type="button"
                  className="scopeSwitcherUtility"
                  onClick={() => {
                    selectNav('applications');
                    setWorkspaceMenuOpen(false);
                  }}
                >
                  View and manage projects
                </button>
              </section>
            </div>
            <div className="scopeSwitcherFooter">
              <button
                type="button"
                onClick={() => {
                  selectNav('settings');
                  setWorkspaceMenuOpen(false);
                }}
              >
                Workspace settings
              </button>
              <span>
                {activeOrganization?.name || 'No workspace'} /{' '}
                {activeProject?.name || 'No project'}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="topbarRight">
        <button
          type="button"
          className="topbarUserBtn"
          onClick={() => {
            setUserMenuOpen((open) => !open);
            setWorkspaceMenuOpen(false);
            setAppMenuOpen(false);
            setEnvMenuOpen(false);
          }}
          aria-expanded={userMenuOpen}
        >
          <span className="topbarUserAvatar">{profileUser.avatar}</span>
          <span className="topbarUserName">{profileUser.name}</span>
          <ChevronDown
            size={12}
            className={`topbarOrgCaret${userMenuOpen ? ' open' : ''}`}
          />
        </button>

        {userMenuOpen ? (
          <div className="topbarUserMenu">
            <div className="topbarUserMenuHeader">
              <strong>{profileUser.name}</strong>
              <span>{profileUser.email}</span>
            </div>
            <div className="topbarUserMenuDivider" />
            <button
              type="button"
              className="topbarUserMenuItem"
              onClick={() => {
                setUserMenuOpen(false);
                onOpenAccountSecurity();
              }}
            >
              Account security
            </button>
            <button
              type="button"
              className="topbarUserMenuItem topbarUserMenuItem--danger"
              onClick={onSignOut}
            >
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
});

export { Topbar };
